/**
 * What is on the canvas, as the decisions behind it (TT-1227 review
 * cassette). `TapestryCanvas` emits a frame through `onPaint` whenever what
 * it paints changes, and paints one back through `playback`.
 *
 * A frame is decisions, not pixels: enough to paint the same picture again
 * through the same components, and nothing the renderer would re-derive.
 *
 *   at          the reducer's clock: the stream index of the last sentence
 *               replayed (`state.playheadSent`)
 *   sent        the host's playhead ordinal (`currentSent`)
 *   phase       empty | generative | curated
 *   background  { id, primarySymbolId } while curated, else null
 *   placements  generative: the packer's placements as painted (symbolId, role,
 *               x, y, side, sentence, fontSize, curve, layout), without the
 *               symbol's reducer entry
 *   slots       curated: [{ i, symbolId?, phrase?, fallbacks? }], the symbol
 *               and phrase each layout slot held, by slot position
 *               (lib/fillSlots replayFill takes this back)
 *   canvas      { width, height, fullscreen } in CSS pixels
 *
 * Why not reproduce from the spine: the reducer is pure over the segments,
 * but the segments of a live tap are provisional (reannotate replaces them),
 * and everything after the reducer is path-dependent (the packer keeps the
 * previous frame's positions, the fill keeps slot memory and dwell, both
 * depend on the canvas size and the font). What was seen can only be
 * recorded.
 */

const EMPTY = [];

export function paintFrame({ playheadSent, currentSent, showCurated, composition, curatedDetail, placements, canvasSize, isFullscreen }) {
  const canvas = {
    width: Math.round(canvasSize?.width || 0),
    height: Math.round(canvasSize?.height || 0),
    fullscreen: !!isFullscreen,
  };
  const at = playheadSent ?? -1;
  const sent = currentSent ?? -1;
  if (showCurated && composition && curatedDetail) {
    return {
      at,
      sent,
      phase: 'curated',
      background: { id: curatedDetail.id, primarySymbolId: curatedDetail.primarySymbolId || '' },
      slots: (composition.filledSlots || EMPTY).map(recordedSlot).filter(Boolean),
      canvas,
    };
  }
  const list = placements || EMPTY;
  return {
    at,
    sent,
    phase: list.length ? 'generative' : 'empty',
    background: null,
    placements: list.map(recordedPlacement),
    canvas,
  };
}

function recordedSlot(slot) {
  if (!slot || (!slot.symbolId && !slot.phrase)) return null;
  const r = { i: slot.idx };
  if (slot.symbolId) r.symbolId = slot.symbolId;
  if (slot.phrase) r.phrase = slot.phrase;
  if (slot.phraseFallbacks?.length) r.fallbacks = slot.phraseFallbacks;
  return r;
}

// The reducer entry (the symbol's sentence buffer) is the one bulky field a
// placement carries that painting does not read.
function recordedPlacement(p) {
  const rest = { ...p };
  delete rest.entry;
  delete rest.exiting;
  delete rest.exitedAt;
  return rest;
}

/**
 * The part of a frame that is the picture, as a string: equal strings mean
 * the same thing on screen, so an emitter fires on a change of signature and
 * not on every tick (the packer returns a fresh array each sentence).
 */
export function paintSignature(frame) {
  if (!frame) return '';
  // paintFrame builds stable field order. Compare every captured decision,
  // including precise geometry and fallbacks, while clocks may advance alone.
  const picture = { ...frame };
  delete picture.at;
  delete picture.sent;
  return JSON.stringify(picture);
}
